import { Injectable, signal, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  readonly isDark = signal(false);

  constructor() {
    if (this.isBrowser) {
      // Même logique que theme-init.js : light par défaut, sombre uniquement
      // si l'utilisateur l'a explicitement choisi (jamais prefers-color-scheme).
      this.isDark.set(localStorage.getItem('theme') === 'dark');
      this.applyTheme();
    }

    effect(() => {
      this.applyTheme();
    });
  }

  toggle(): void {
    const nextTheme = !this.isDark();
    const commitTheme = (): void => {
      this.isDark.set(nextTheme);
      this.applyTheme();
      if (this.isBrowser) {
        localStorage.setItem('theme', nextTheme ? 'dark' : 'light');
      }
    };

    if (!this.isBrowser || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      commitTheme();
      return;
    }

    const transitionDocument = document as Document & {
      startViewTransition?: (update: () => void) => { finished: Promise<void> };
    };
    if (transitionDocument.startViewTransition) {
      void transitionDocument.startViewTransition(commitTheme).finished.catch(() => undefined);
      return;
    }

    commitTheme();
  }

  private applyTheme(): void {
    if (this.isBrowser) {
      document.documentElement.classList.toggle('dark', this.isDark());
    }
  }
}
